<script lang="ts" setup>
  import Button from '@leaflink/stash/Button.vue';
  import Input from '@leaflink/stash/Input.vue';
  import Modal from '@leaflink/stash/Modal.vue';
  import useValidation, { email, ValidationRules } from '@leaflink/stash/useValidation';
  import { ref } from 'vue';

  import i18n from '@/plugins/i18n';
  import { useCompanyBillingPatch } from '@/queries/settings/companyBilling.query';

  import { useInventoryInsightsProvider } from '../InventoryInsights.vue';

  interface BillingContactModalProps {
    open: boolean;
  }

  interface BillingEmailState {
    email: string;
  }

  function getInitialEmailState(): BillingEmailState {
    return {
      email: '',
    };
  }

  const { billingRequestArgs } = useInventoryInsightsProvider();
  const isUpdating = ref(false);

  const validationRules: ValidationRules<BillingEmailState> = {
    email: [email({ message: i18n.t('inventoryInsights.validateEmail') })],
  };

  const billingEmailState = ref<BillingEmailState>(getInitialEmailState());

  const validation = useValidation({ rules: validationRules, values: billingEmailState });

  const props = withDefaults(defineProps<BillingContactModalProps>(), {
    open: false,
  });

  const emit = defineEmits<{
    (e: 'close'): void;
  }>();

  const { mutateAsync } = useCompanyBillingPatch();

  async function updateCompanyBillingEmail() {
    isUpdating.value = true;
    await validation.validate();

    if (validation.hasErrors) {
      return;
    }

    if (billingRequestArgs) {
      billingRequestArgs.billingEmail = billingEmailState.value.email;
    }

    try {
      await mutateAsync(billingRequestArgs);
    } finally {
      isUpdating.value = false;
      emit('close');
    }
  }
</script>

<template>
  <Teleport to="#stash-modals-mount-node">
    <Modal
      data-test="modal|billing-contact-modal"
      :open="props.open"
      :title="i18n.t('inventoryInsights.billingSetup.editBillingContact')"
      @dismiss="emit('close')"
    >
      <Input
        v-model="billingEmailState.email"
        data-test="input|email-address"
        type="text"
        :error-text="validation.getError('email')"
        :label="i18n.t('inventoryInsights.enterEmailAddress')"
        :placeholder="'example@leaflink.com'"
      />
      <template #actions>
        <div class="tw-flex tw-w-full tw-justify-between tw-gap-6">
          <Button class="tw-w-full" data-test="exit|billing-contact-modal" secondary @click="emit('close')">
            {{ i18n.t('common.exit') }}
          </Button>
          <Button
            class="tw-w-full"
            data-test="save|billing-contact-modal"
            :disabled="billingEmailState.email.length === 0"
            :is-loading="isUpdating"
            @click="updateCompanyBillingEmail"
          >
            {{ i18n.t('common.save') }}
          </Button>
        </div>
      </template>
    </Modal>
  </Teleport>
</template>
